import React, { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  CalendarDaysIcon,
  VideoCameraIcon,
  EnvelopeIcon,
  BellAlertIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const GoogleAPIUsage = () => {
  const [scopeData, setScopeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchScopeInfo = async () => {
      try {
        const response = await api.get('/calendar/google/api-scopes');
        setScopeData(response.data);
      } catch (err) {
        console.error('Failed to load scope info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScopeInfo();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-6">
      {/* Header */}
      <div className="text-center">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-blue-600 dark:text-blue-400" />
        <h1 className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">
          Google API Permissions
        </h1>
        <p className="mt-2 text-lg text-gray-500 dark:text-gray-400">
          How EasyLaw uses Google services and what data we access
        </p>
      </div>

      {/* Scope Detail Card */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Requested OAuth Scope
          </h2>
        </div>
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 h-12 w-12 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center">
              <CalendarDaysIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Google Calendar Events
              </h3>
              <code className="mt-1 inline-block px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono text-gray-700 dark:text-gray-300">
                https://www.googleapis.com/auth/calendar.events
              </code>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {scopeData?.scopes_requested?.[0]?.justification ||
                  'Allows EasyLaw to create calendar events and generate Google Meet video conference links for client consultations.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features That Use This Scope */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Features Using This Permission
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Each feature below requires the calendar.events scope to function
          </p>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          <div className="p-6 flex items-start space-x-4">
            <div className="flex-shrink-0 h-10 w-10 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center">
              <CalendarDaysIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Create Consultation Events
              </h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Lawyers schedule client consultations from the Diary page. EasyLaw creates
                a corresponding event on the lawyer's Google Calendar with the meeting title,
                date, time, and description.
              </p>
            </div>
          </div>

          <div className="p-6 flex items-start space-x-4">
            <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center">
              <VideoCameraIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Auto-Generate Google Meet Links
              </h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                When creating a consultation event, EasyLaw requests a Google Meet conference
                link via the Calendar API's conference data feature. The Meet link is displayed
                in the diary entry so both the lawyer and client can join the video call.
              </p>
            </div>
          </div>

          <div className="p-6 flex items-start space-x-4">
            <div className="flex-shrink-0 h-10 w-10 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center">
              <EnvelopeIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Send Calendar Invitations to Clients
              </h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                The client's email is added as an event attendee. Google Calendar automatically
                sends an invitation email with the meeting details and Meet link so the client
                receives all information needed to attend.
              </p>
            </div>
          </div>

          <div className="p-6 flex items-start space-x-4">
            <div className="flex-shrink-0 h-10 w-10 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg flex items-center justify-center">
              <BellAlertIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Set Meeting Reminders
              </h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Each created event includes reminders: an email reminder 60 minutes before
                and a popup reminder 15 minutes before the consultation to ensure neither
                party misses the meeting.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* OAuth Consent Screen Flow */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            OAuth Consent Flow
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step-by-step process when connecting Google Calendar
          </p>
        </div>
        <div className="p-6">
          <ol className="space-y-4">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white text-sm font-bold rounded-full flex items-center justify-center">1</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">User clicks "Connect Google Calendar"</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">On the Diary page, the lawyer initiates the connection. A dialog explains what permission is being requested.</p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white text-sm font-bold rounded-full flex items-center justify-center">2</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Google OAuth Consent Screen</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">The user is redirected to Google's consent screen showing that EasyLaw is requesting access to "View and edit events on all your calendars" (calendar.events scope).</p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white text-sm font-bold rounded-full flex items-center justify-center">3</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">User grants permission</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">The user reviews the scope and clicks "Allow". Google redirects back to EasyLaw with an authorization code.</p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white text-sm font-bold rounded-full flex items-center justify-center">4</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Token exchange & storage</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">EasyLaw exchanges the auth code for access and refresh tokens. Tokens are encrypted with Fernet and stored securely.</p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-7 h-7 bg-green-600 text-white text-sm font-bold rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-4 w-4" />
              </span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Connection confirmed</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">The user is redirected back to the Diary page with a success message. They can now schedule consultations with Google Meet links.</p>
              </div>
            </li>
          </ol>
        </div>
      </div>

      {/* Data Security & Privacy */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <LockClosedIcon className="h-5 w-5 mr-2 text-green-600" />
            Data Security & Privacy
          </h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-200">Token Encryption</h4>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                All OAuth tokens are encrypted at rest using Fernet symmetric encryption before storage.
              </p>
            </div>
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-200">Minimal Data Access</h4>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                EasyLaw only requests the calendar.events scope — we cannot read email, contacts, or files.
              </p>
            </div>
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-200">User-Initiated Only</h4>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                Calendar events are only created when the lawyer explicitly schedules a consultation. No background access.
              </p>
            </div>
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-200">Easy Revocation</h4>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                Users can disconnect at any time from the Diary page. Tokens are immediately deleted upon disconnection.
              </p>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            <p className="flex items-center">
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
              You can also revoke access directly from your{' '}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 text-blue-600 dark:text-blue-400 hover:underline"
              >
                Google Account permissions page
              </a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleAPIUsage;
